import React, { useEffect, useRef, useState } from "react";
import { LogoPulse } from "./Loader";
import { useLocation } from "react-router-dom";
import { RefreshCw, ArrowDown, ArrowLeft, ArrowRight } from "lucide-react";

// Mobile pull gesture from the TOP of the page:
//   pull down            → refresh
//   pull down, then left → back to the previous page
//   pull down, then right→ forward (only after going back with this gesture)
const MAX_PULL = 96;
const ARM_PULL = 64;
const SIDE = 56;
const FWD_KEY = "rosterly_pull_fwd";
const getFwd = () => { try { return Number(sessionStorage.getItem(FWD_KEY) || 0); } catch { return 0; } };
const setFwd = (n) => { try { sessionStorage.setItem(FWD_KEY, String(Math.max(0, n))); } catch { /* noop */ } };

export default function MobilePullRefresh() {
    const [pull, setPull] = useState(0);
    const [dir, setDir] = useState(null); // null | "back" | "fwd"
    const [busy, setBusy] = useState(null);
    const st = useRef({ active: false, startY: 0, startX: 0, armedX: null });
    const byGesture = useRef(false);
    const live = useRef({ pull: 0, dir: null });
    const reloadT = useRef(null);
    const setPullL = (v) => { live.current.pull = v; setPull(v); };
    const setDirL = (v) => { live.current.dir = v; setDir(v); };
    const location = useLocation();

    // Any navigation NOT made by this gesture clears the forward stack (like a browser).
    useEffect(() => {
        if (byGesture.current) { byGesture.current = false; setBusy(null); return; }
        setFwd(0);
    }, [location.key]);

    useEffect(() => {
        const isMobile = () => { try { return window.matchMedia("(max-width: 1024px)").matches || ("ontouchstart" in window); } catch { return false; } };
        const atTop = () => window.scrollY <= 0;
        const canBack = () => window.history.length > 1 && (window.history.state?.idx ?? 1) > 0;
        const onStart = (e) => {
            // Touching again while "Refreshing…" is pending cancels the refresh.
            if (busy === "refresh" && reloadT.current) { clearTimeout(reloadT.current); reloadT.current = null; setBusy(null); st.current.active = false; return; }
            if (!isMobile() || busy || e.touches.length !== 1 || !atTop()) { st.current.active = false; return; }
            if (e.target.closest && e.target.closest(".overflow-y-auto, .overflow-auto, [data-noswipe], [role=dialog]")) { st.current.active = false; return; }
            const t = e.touches[0];
            st.current = { active: true, startY: t.clientY, startX: t.clientX, armedX: null };
        };
        const onMove = (e) => {
            const s = st.current;
            if (!s.active || busy) return;
            const t = e.touches[0];
            const dy = t.clientY - s.startY;
            const p = Math.min(MAX_PULL, Math.max(0, dy * 0.6));
            if (s.armedX === null) {
                if (dy > 0 && Math.abs(t.clientX - s.startX) < dy && atTop()) setPullL(p); else if (dy <= 0) setPullL(0);
                if (p >= ARM_PULL) s.armedX = t.clientX;   // armed: now sideways picks back/forward
                return;
            }
            // Swiping back up below the arm point cancels (nothing happens on release).
            if (p < ARM_PULL) { s.armedX = null; setPullL(p); setDirL(null); return; }
            const dx = t.clientX - s.armedX;
            setPullL(p);
            if (dx <= -SIDE && canBack()) setDirL("back");
            else if (dx >= SIDE && getFwd() > 0) setDirL("fwd");
            else setDirL(null);
        };
        const onEnd = () => {
            const s = st.current;
            s.active = false;
            const { pull: cur, dir: d } = live.current;
            setPullL(0); setDirL(null);
            if (cur < ARM_PULL) return;
            if (d === "back") { setBusy("back"); byGesture.current = true; setFwd(getFwd() + 1); window.history.back(); }
            else if (d === "fwd") { setBusy("fwd"); byGesture.current = true; setFwd(getFwd() - 1); window.history.forward(); }
            else { setBusy("refresh"); reloadT.current = setTimeout(() => { reloadT.current = null; window.location.reload(); }, 700); }
        };
        window.addEventListener("touchstart", onStart, { passive: true });
        window.addEventListener("touchmove", onMove, { passive: true });
        window.addEventListener("touchend", onEnd, { passive: true });
        window.addEventListener("touchcancel", onEnd, { passive: true });
        return () => {
            window.removeEventListener("touchstart", onStart);
            window.removeEventListener("touchmove", onMove);
            window.removeEventListener("touchend", onEnd);
            window.removeEventListener("touchcancel", onEnd);
        };
    }, [busy]);

    // Safety: never leave the pill stuck if a back/forward didn't change the route.
    useEffect(() => { if (busy === "back" || busy === "fwd") { const t = setTimeout(() => setBusy(null), 1200); return () => clearTimeout(t); } return undefined; }, [busy]);

    const armed = pull >= ARM_PULL;
    if (pull <= 0 && !busy) return null;
    const label = busy === "refresh" ? <><LogoPulse className="h-4 w-4" /> Refreshing… tap to cancel</>
        : busy === "back" ? <><ArrowLeft className="h-4 w-4" /> Going back…</>
        : busy === "fwd" ? <>Going forward… <ArrowRight className="h-4 w-4" /></>
        : dir === "back" ? <><ArrowLeft className="h-4 w-4" /> Release to go back</>
        : dir === "fwd" ? <>Release to go forward <ArrowRight className="h-4 w-4" /></>
        : armed ? <><RefreshCw className="h-4 w-4" /> Release to refresh · ↑ cancel · ← back{getFwd() > 0 ? " · fwd →" : ""}</>
        : <><ArrowDown className="h-4 w-4" /> Keep pulling down</>;
    return (
        <div className="fixed inset-x-0 top-0 z-[120] flex justify-center pointer-events-none lg:hidden"
            style={{ transform: `translateY(${busy ? 0 : Math.min(0, pull - 40)}px)` }} data-testid="mobile-pull-refresh">
            <div data-testid="mobile-pull-pill" data-dir={dir || (busy || "")}
                className={`mt-[calc(env(safe-area-inset-top)+12px)] inline-flex items-center gap-2 px-4 py-2.5 rounded-full border shadow-xl shadow-black/50 text-xs font-black uppercase tracking-wider transition-[background-color,border-color,color,transform] duration-150 ${dir === "back" ? "-translate-x-6" : dir === "fwd" ? "translate-x-6" : ""} ${busy || armed ? "bg-[#9D4CDD] border-[#9D4CDD] text-white" : "bg-[#12131A] border-white/15 text-white/70"}`}>
                {label}
            </div>
        </div>
    );
}
